// src/components/report/TeacherFeedbackPage.jsx - Updated with real images for proficiency diagram
import React from 'react';
import HappyIcon from '../../../../Assets/Foundational/picsvg_download(1).svg';
import NeutralIcon from '../../../../Assets/Foundational/picsvg_download(2).svg';
import QuestionIcon from '../../../../Assets/Foundational/picsvg_download(3).svg';
import ClassmateIcon from '../../../../Assets/Foundational/picsvg_download(4).svg';
import TeacherIcon from '../../../../Assets/Foundational/picsvg_download(5).svg';
import BooksIcon from '../../../../Assets/Foundational/picsvg_download(6).svg';
import ComputerIcon from '../../../../Assets/Foundational/picsvg_download(7).svg';
import NoneIcon from '../../../../Assets/Foundational/picsvg_download(8).svg';
import News from '../../../../Assets/Foundational/picsvg_download(10).svg';
import Mens from '../../../../Assets/Foundational/picsvg_download(11).svg';
import System from '../../../../Assets/Foundational/picsvg_download(12).svg';
import World from '../../../../Assets/Foundational/picsvg_download(13).svg';

// Fixed pixel icon style - prevents html2canvas DPI scaling issues
const ICON_SIZE = '24px';
const ICON_SIZE_LG = '32px';

// Helper to generate selection border style - uses 2px solid border instead of ring/shadows
const getSelectionBorderStyle = (isSelected, color = '#3B82F6') => {
    if (!isSelected) return {};
    return {
        border: `2px solid ${color}`,
        borderRadius: '50%',
        boxSizing: 'border-box'
    };
};

// Proficiency Diagram with HTML/CSS implementation for reliable PDF rendering
// Switching away from pure SVG <image> tags which often fail in html2canvas/PDF exports
const ProficiencyDiagram = ({ proficiencyMarks }) => {
    // Helper to check if a level should be highlighted
    const isHighlighted = (level) => {
        return proficiencyMarks?.some(mark => mark.level === level);
    };

    const skyImageUrl = "https://images.unsplash.com/photo-1657598339759-fd1432d833f0?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Y2xvdWRzJTIwaW4lMjBza3l8ZW58MHx8MHx8fDA%3D";
    const mountainImageUrl = "https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/Close_Up_View_of_Mount_Everest_from_Kala_Patthar_%285644_m%29_in_2023-IMG-3485.jpg/1000px-Close_Up_View_of_Mount_Everest_from_Kala_Patthar_%285644_m%29_in_2023-IMG-3485.jpg";
    // Changed to a reliable Unsplash source for Stream
    const streamImageUrl = "https://images.unsplash.com/photo-1437482078695-73f5ca6c96e2?q=80&w=1000&auto=format&fit=crop";

    return (
        <div className="flex items-center justify-center p-2 gap-4">
            {/* Left Side: Arrow and Label */}
            <div className="flex flex-col items-center justify-between relative" style={{ height: '160px', width: '30px' }}>
                {/* Arrow SVG */}
                <svg width="30" height="170" viewBox="0 0 30 170" fill="none" className="absolute top-0 left-0 h-full w-full">
                    <line x1="15" y1="168" x2="15" y2="10" stroke="#5B21B6" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    <defs>
                        <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                            <polygon points="0 0, 10 3.5, 0 7" fill="#5B21B6" />
                        </marker>
                    </defs>
                </svg>

                {/* Rotated Text using CSS transform for better sharpness */}
                <div style={{
                    transform: 'rotate(-90deg)',
                    whiteSpace: 'nowrap',
                    color: '#5B21B6',
                    fontSize: '11px',
                    fontWeight: 800,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transformOrigin: 'center center',
                    marginTop: '-10px',
                    marginLeft: '-50px'
                }}>
                    Levels of Proficiency
                </div>
            </div>


            {/* Right Side: Circular Image Diagram */}
            <div className="relative rounded-full overflow-hidden shadow-sm bg-white" style={{ width: '160px', height: '160px', border: '2px solid #4B5563' }}>

                {/* 1. SKY SECTION (Top 33%) */}
                <div
                    className="absolute left-0 w-full overflow-hidden"
                    style={{ top: 0, height: '33.33%', borderBottom: '1px solid #9CA3AF' }}
                >
                    <img
                        src={skyImageUrl}
                        alt="Sky"
                        crossOrigin="anonymous"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div className="absolute left-0 w-full text-center" style={{ top: '8px' }}>
                        <span className="font-bold rounded-sm" style={{
                            color: '#1E3A8A',
                            fontSize: '12px',
                            textShadow: '0px 0px 2px white',
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            padding: '0 8px'
                        }}>Sky</span>
                    </div>
                    {/* Explicit Border Overlay for Selection */}
                    {isHighlighted('Sky') && (
                        <div className="absolute inset-0 pointer-events-none" style={{ border: '3px solid #EF4444', zIndex: 10 }}></div>
                    )}
                </div>

                {/* 2. MOUNTAIN SECTION (Middle 33%) */}
                <div
                    className="absolute left-0 w-full overflow-hidden"
                    style={{ top: '33.33%', height: '33.33%', borderBottom: '1px solid #9CA3AF' }}
                >
                    <img
                        src={mountainImageUrl}
                        alt="Mountain"
                        crossOrigin="anonymous"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div className="absolute left-0 w-full text-center" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                        <span className="font-bold" style={{
                            color: 'white',
                            fontSize: '12px',
                            textShadow: '1px 1px 2px black'
                        }}>Mountain</span>
                    </div>
                    {/* Explicit Border Overlay for Selection */}
                    {isHighlighted('Mountain') && (
                        <div className="absolute inset-0 pointer-events-none" style={{ border: '3px solid #EF4444', zIndex: 10 }}></div>
                    )}
                </div>

                {/* 3. STREAM SECTION (Bottom 33%) */}
                <div
                    className="absolute left-0 w-full overflow-hidden"
                    style={{ bottom: 0, height: '33.34%' }}
                >
                    <img
                        src={streamImageUrl}
                        alt="Stream"
                        crossOrigin="anonymous"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div className="absolute left-0 w-full text-center" style={{ bottom: '8px' }}>
                        <span className="font-bold rounded-sm" style={{
                            color: '#064E3B',
                            fontSize: '12px',
                            textShadow: '0px 0px 2px white',
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            padding: '0 8px'
                        }}>Stream</span>
                    </div>
                    {/* Explicit Border Overlay for Selection */}
                    {isHighlighted('Stream') && (
                        <div className="absolute inset-0 pointer-events-none" style={{ border: '3px solid #EF4444', zIndex: 10 }}></div>
                    )}
                </div>

                {/* Inner overlay for glass effect/definition */}
                <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: '1px solid rgba(0,0,0,0.1)' }}></div>
            </div>
        </div>
    );
};

const TeacherFeedbackPage = ({ assignment }) => {
    if (!assignment) return <div className="report-page">Loading Feedback...</div>;

    // Extract assessment data
    const selfAssessment = assignment.self_assessment || {};
    const peerAssessment = assignment.peer_assessment || {};
    const caregiverObservation = assignment.caregiver_observation || {};
    const proficiencyMarks = assignment.proficiency_marks || [];

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            minHeight: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '60px solid #F97316',
                    borderLeft: '60px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '60px solid #F97316',
                    borderRight: '60px solid transparent'
                }}>
            </div>

            <div className="w-full flex flex-col items-center pt-2">
                {/* Orange Header */}
                <div className="w-[850px] max-w-full">
                    <table className="w-full">
                        <tbody>
                            <div className="border border-[#E87C21] rounded-t-[1px]">
                                <tr className="border border-[#E87C21]">
                                    <td colSpan="2"
                                        className="bg-[#E87C21] text-white text-[18px] font-bold text-center py-1.5"
                                        style={{ letterSpacing: 0.2 }}>
                                        TEACHER'S FEEDBACK
                                    </td>
                                </tr>

                                <tr className="border border-[#E87C21]">
                                    <td className="w-[40%] border-r border-[#E87C21] bg-[#fde9d9]  font-bold text-[#A05A09] text-[14px]">
                                        <span className="font-bold p-1 text-[#E87C21]">NOTE:</span>
                                        <span className="font-normal text-[#9c6706] p-1 text-[13px]">For each ability, mark the appropriate level</span>
                                    </td>
                                    <td className="w-[60%] bg-[#fde9d9] font-bold text-center text-[#A05A09] text-[14px]"
                                        style={{ letterSpacing: 0.3 }}>
                                        Observational Notes
                                    </td>
                                </tr>

                                <tr className="border border-[#E87C21]">
                                    <td className="border-r border-[#E87C21] bg-[#ffffff] align-top">
                                        <div className="flex items-center justify-center p-3">
                                            <ProficiencyDiagram proficiencyMarks={proficiencyMarks} />
                                        </div>
                                    </td>
                                    <td className="bg-[#ffffff] p-2 pt-6 align-top">
                                        <div
                                            className="bg-white border rounded border-gray-300 p-3 min-h-[140px] text-[13px] font-normal">
                                            {assignment.observational_notes || assignment.teacher_feedback || ''}
                                        </div>
                                    </td>
                                </tr>

                                <tr className="border border-[#E87C21]">
                                    <td colSpan="2" className="p-2">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span
                                                className="rounded-full border border-black flex items-center justify-center font-serif font-bold"
                                                style={{ width: ICON_SIZE, height: ICON_SIZE, fontSize: '14px' }}>O</span>
                                            <span className="text-[14px] font-normal text-black">Circle the picture that shows how you worked on this activity.</span>
                                        </div>
                                        <div>
                                            <div className="flex w-full text-[13px] font-normal">
                                                <div
                                                    className="w-[16%] bg-[#735ab8] text-white py-1 px-2 font-bold text-center text-[13px] flex items-center justify-center rounded-[20px]">
                                                    Self Assessment
                                                </div>

                                                <div className="w-[20%] px-1.5 py-1  bg-white">
                                                    <div
                                                        className="font-bold rounded p-2 bg-[#54be06] text-center text-[13px] text-white">I
                                                        liked doing this
                                                        work.
                                                    </div>
                                                    <div className="flex p-2 rounded bg-[#f1f9e1] justify-between mt-1">
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={HappyIcon}
                                                                alt="happy"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.liked === true ? getSelectionBorderStyle(true, '#22C55E') : {})
                                                                }}
                                                            />
                                                            yes
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={NeutralIcon}
                                                                alt="neutral"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.liked === false ? getSelectionBorderStyle(true, '#EAB308') : {})
                                                                }}
                                                            />
                                                            no
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={QuestionIcon}
                                                                alt="question"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.liked === null ? getSelectionBorderStyle(true, '#6B7280') : {})
                                                                }}
                                                            />
                                                            do not<br />know
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="w-[20%] px-1.5 py-1 bg-white">
                                                    <div
                                                        className="font-bold p-2 rounded bg-[#ed6cac] text-center text-[13px] text-white">
                                                        I found this work easy.
                                                    </div>
                                                    <div className="flex p-2 rounded bg-[#feedf7] justify-between mt-1">
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={HappyIcon}
                                                                alt="happy"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.easy === true ? getSelectionBorderStyle(true, '#ed6cac') : {})
                                                                }}
                                                            />
                                                            yes
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={NeutralIcon}
                                                                alt="neutral"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.easy === false ? getSelectionBorderStyle(true, '#ed6cac') : {})
                                                                }}
                                                            />
                                                            no
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={QuestionIcon}
                                                                alt="question"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.easy === null ? getSelectionBorderStyle(true, '#ed6cac') : {})
                                                                }}
                                                            />
                                                            do not<br />know
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="w-[44%] px-1.5 py-1 bg-white">
                                                    <div
                                                        className="font-bold p-4 rounded text-white text-center bg-[#4ab7de] text-[13px]">
                                                        To do this work, I needed…
                                                    </div>
                                                    <div
                                                        className="flex rounded pt-4 pb-4 bg-[#eef6fe] justify-around mt-1">
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={ClassmateIcon}
                                                                alt="classmate"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.needed?.includes('classmate') ? getSelectionBorderStyle(true, '#4ab7de') : {})
                                                                }}
                                                            />
                                                            Classmate
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={TeacherIcon}
                                                                alt="teacher"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.needed?.includes('teacher') ? getSelectionBorderStyle(true, '#4ab7de') : {})
                                                                }}
                                                            />
                                                            teacher
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={BooksIcon}
                                                                alt="books"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.needed?.includes('books') ? getSelectionBorderStyle(true, '#4ab7de') : {})
                                                                }}
                                                            />
                                                            books
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={ComputerIcon}
                                                                alt="computer"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.needed?.includes('computer') ? getSelectionBorderStyle(true, '#4ab7de') : {})
                                                                }}
                                                            />
                                                            computer
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={NoneIcon}
                                                                alt="none"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(selfAssessment.needed?.includes('none') ? getSelectionBorderStyle(true, '#4ab7de') : {})
                                                                }}
                                                            />
                                                            none
                                                        </span>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </td>
                                </tr>

                                <tr className="border border-[#E87C21]">
                                    <td colSpan="2" className="p-2">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span
                                                className="rounded-full border border-black flex items-center justify-center font-serif font-bold"
                                                style={{ width: ICON_SIZE, height: ICON_SIZE, fontSize: '14px' }}>O</span>
                                            <span className="text-[14px] font-normal text-black">Circle the picture that shows how your friend worked on this activity.</span>
                                        </div>
                                        <div>
                                            <div className="flex w-full text-[13px] font-normal">
                                                <div
                                                    className="w-[16%] rounded-[20px] bg-[#1b63af] text-white py-1 px-2 font-bold text-center  text-[13px] flex items-center justify-center">
                                                    Peer Assessment
                                                </div>
                                                <div className="w-[20%] px-1.5 py-1 bg-white">
                                                    <div
                                                        className="font-bold p-2 rounded bg-[#00a558] text-center text-[13px] text-white">
                                                        My friend liked doing this work.
                                                    </div>
                                                    <div className="flex p-2 rounded bg-[#f1f9e1] justify-between mt-1">
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={HappyIcon}
                                                                alt="happy"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_liked === true ? getSelectionBorderStyle(true, '#00a558') : {})
                                                                }}
                                                            />
                                                            yes
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={NeutralIcon}
                                                                alt="neutral"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_liked === false ? getSelectionBorderStyle(true, '#00a558') : {})
                                                                }}
                                                            />
                                                            no
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={QuestionIcon}
                                                                alt="question"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_liked === null ? getSelectionBorderStyle(true, '#00a558') : {})
                                                                }}
                                                            />
                                                            do not<br />know
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="w-[20%] px-1.5 py-1 bg-white">
                                                    <div
                                                        className="font-bold p-2 rounded bg-[#e10485] text-center text-[13px] text-white">
                                                        My friend found this work easy.
                                                    </div>
                                                    <div className="flex p-2 rounded bg-[#feedf7] justify-between mt-1">
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={HappyIcon}
                                                                alt="happy"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_easy === true ? getSelectionBorderStyle(true, '#e10485') : {})
                                                                }}
                                                            />
                                                            yes
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={NeutralIcon}
                                                                alt="neutral"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_easy === false ? getSelectionBorderStyle(true, '#e10485') : {})
                                                                }}
                                                            />
                                                            no
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={QuestionIcon}
                                                                alt="question"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_easy === null ? getSelectionBorderStyle(true, '#e10485') : {})
                                                                }}
                                                            />
                                                            do not<br />know
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="w-[44%] px-1.5 py-1 bg-white">
                                                    <div
                                                        className="font-bold p-4 text-white text-center rounded bg-[#0092cb] text-[13px]">
                                                        To do this work, my friend needed…
                                                    </div>
                                                    <div
                                                        className="flex pt-4 pb-4 rounded bg-[#eef6fe] justify-around mt-1">
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={ClassmateIcon}
                                                                alt="classmate"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_needed?.includes('classmate') ? getSelectionBorderStyle(true, '#0092cb') : {})
                                                                }}
                                                            />
                                                            Classmate
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={TeacherIcon}
                                                                alt="teacher"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_needed?.includes('teacher') ? getSelectionBorderStyle(true, '#0092cb') : {})
                                                                }}
                                                            />
                                                            teacher
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={BooksIcon}
                                                                alt="books"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_needed?.includes('books') ? getSelectionBorderStyle(true, '#0092cb') : {})
                                                                }}
                                                            />
                                                            books
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={ComputerIcon}
                                                                alt="computer"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_needed?.includes('computer') ? getSelectionBorderStyle(true, '#0092cb') : {})
                                                                }}
                                                            />
                                                            computer
                                                        </span>
                                                        <span className="flex flex-col items-center text-xs text-black">
                                                            <img
                                                                src={NoneIcon}
                                                                alt="none"
                                                                style={{
                                                                    width: ICON_SIZE,
                                                                    height: ICON_SIZE,
                                                                    marginBottom: '2px',
                                                                    ...(peerAssessment.friend_needed?.includes('none') ? getSelectionBorderStyle(true, '#0092cb') : {})
                                                                }}
                                                            />
                                                            none
                                                        </span>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </div>

                        </tbody>
                    </table>

                </div>

                {/* =================================================================== */}
                {/* Parents/Caregiver/Guardian's Observation                            */}
                {/* =================================================================== */}
                <div className="w-[850px] max-w-full border border-[#E87C21] mt-10">
                    {/* Header Row */}
                    <div className="flex border-b border-[#E87C21]">
                        <div className="w-[70%] border-r border-[#E87C21] bg-[#fabf8f] px-4 py-2">
                            <h3 className="font-bold text-center text-[#C15816] text-[15px]">Parents/Caregiver/Guardian's
                                Observation</h3>
                        </div>
                        <div className="w-[30%] bg-[#fabf8f] px-4 py-2">
                            <h3 className="font-bold text-center text-[#C15816] text-[15px]">Comments/Remarks</h3>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex">
                        {/* Left Column: Observation Details */}
                        <div className="w-[70%] border-r border-[#E87C21] bg-[#ffffff] px-4 pt-2 pb-4">
                            <div className="flex items-center mb-2 text-[14px]">
                                <span
                                    className="rounded-full border border-black flex items-center justify-center font-serif font-bold mr-2"
                                    style={{ width: ICON_SIZE, height: ICON_SIZE, fontSize: '14px' }}>O</span>
                                <span className="text-[14px]">Circle the relevant response</span>
                            </div>

                            {/* Learning Resources Box */}
                            <div className="flex border-2 border-[#3384E6] rounded-lg overflow-hidden min-h-[110px]">
                                {/* Vertical Header */}
                                <div className="flex items-center justify-center bg-[#3384E6] p-2 w-[90px]">
                                    <h4 className="text-white font-bold text-sm text-center">
                                        Learning Teaching resources at home
                                    </h4>
                                </div>

                                {/* Resources Icons and Text */}
                                <div className="flex-grow p-3 bg-[#eef6fe]">
                                    <div className="flex justify-around items-start h-full">
                                        <span
                                            className="flex flex-col items-center justify-start text-center text-xs text-black w-1/5">
                                            <img
                                                src={BooksIcon}
                                                alt="books/magazine"
                                                style={{
                                                    width: ICON_SIZE_LG,
                                                    height: ICON_SIZE_LG,
                                                    marginBottom: '4px',
                                                    ...(caregiverObservation.resources?.includes('books/magazine') ? getSelectionBorderStyle(true, '#3384E6') : {})
                                                }}
                                            />
                                            <span>books/<br />magazine</span>
                                        </span>
                                        <span
                                            className="flex flex-col items-center justify-start text-center text-xs text-black w-1/5">
                                            <img
                                                src={News}
                                                alt="newspaper"
                                                style={{
                                                    width: ICON_SIZE_LG,
                                                    height: ICON_SIZE_LG,
                                                    marginBottom: '4px',
                                                    ...(caregiverObservation.resources?.includes('newspaper') ? getSelectionBorderStyle(true, '#3384E6') : {})
                                                }}
                                            />
                                            <span>newspaper</span>
                                        </span>
                                        <span
                                            className="flex flex-col items-center justify-start text-center text-xs text-black w-1/5">
                                            <img
                                                src={Mens}
                                                alt="toys/games/sports"
                                                style={{
                                                    width: ICON_SIZE_LG,
                                                    height: ICON_SIZE_LG,
                                                    marginBottom: '4px',
                                                    ...(caregiverObservation.resources?.includes('toys/games/sports') ? getSelectionBorderStyle(true, '#3384E6') : {})
                                                }}
                                            />
                                            <span>toys/<br />games/<br />sports</span>
                                        </span>
                                        <span
                                            className="flex flex-col items-center justify-start text-center text-xs text-black w-1/5">
                                            <img
                                                src={System}
                                                alt="phone/computer"
                                                style={{
                                                    width: ICON_SIZE,
                                                    height: ICON_SIZE,
                                                    marginBottom: '2px',
                                                    ...(caregiverObservation.resources?.includes('phone/computer') ? getSelectionBorderStyle(true, '#3384E6') : {})
                                                }}
                                            />
                                            <span>phone/<br />computer</span>
                                        </span>
                                        <span
                                            className="flex flex-col items-center justify-start text-center text-xs text-black w-1/5">
                                            <img
                                                src={World}
                                                alt="internet"
                                                style={{
                                                    width: ICON_SIZE,
                                                    height: ICON_SIZE,
                                                    marginBottom: '2px',
                                                    ...(caregiverObservation.resources?.includes('internet') ? getSelectionBorderStyle(true, '#3384E6') : {})
                                                }}
                                            />
                                            <span>internet</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* Right Column: Comments Box */}
                        <div className="w-[30%] bg-[#ffffff] px-4 pt-8 pb-4">
                            <div
                                className="bg-white border rounded border-gray-300 p-3 min-h-[120px] text-[13px] font-normal">
                                {caregiverObservation.comments || ''}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <div className="border-b-2 border-[#fbcfab] w-full mt-20" />
        </div>
    );
};

export default TeacherFeedbackPage;